/**
 * TalentBanner — Iter-290b Sprint B
 *
 * Renders the talent's customizable profile banner. Three kinds:
 *   • gradient → preset key (cyan-violet default fallback)
 *   • color    → "#rrggbb" solid fill
 *   • image    → background-image cover, with vertical offset hint
 *
 * Falls back to the legacy `cyan-violet` gradient when no banner is set.
 *
 * Preset keys MUST match the VALID_GRADIENT_PRESETS set in
 * /app/backend/routes/talent_banner_routes.py
 */
import React from 'react';

// Tailwind doesn't tree-shake dynamic class strings, so we keep the
// gradient map literal. Each value is the Tailwind class set used by the
// banner div. Adjustments here must mirror the editor preview palette.
export const GRADIENT_PRESETS = {
  'cyan-violet': {
    label: 'Cyan → Violet',
    className: 'bg-gradient-to-r from-cyan-400 via-cyan-300 to-violet-400',
    swatch: 'linear-gradient(90deg, #22d3ee 0%, #67e8f9 50%, #a78bfa 100%)',
  },
  sunset: {
    label: 'Sunset',
    className: 'bg-gradient-to-r from-orange-400 via-rose-400 to-pink-500',
    swatch: 'linear-gradient(90deg, #fb923c 0%, #fb7185 50%, #ec4899 100%)',
  },
  ocean: {
    label: 'Ocean',
    className: 'bg-gradient-to-r from-sky-500 via-cyan-500 to-emerald-500',
    swatch: 'linear-gradient(90deg, #0ea5e9 0%, #06b6d4 50%, #10b981 100%)',
  },
  forest: {
    label: 'Forest',
    className: 'bg-gradient-to-r from-emerald-600 via-green-500 to-lime-400',
    swatch: 'linear-gradient(90deg, #059669 0%, #22c55e 50%, #a3e635 100%)',
  },
  midnight: {
    label: 'Midnight',
    className: 'bg-gradient-to-r from-slate-900 via-indigo-900 to-purple-900',
    swatch: 'linear-gradient(90deg, #0f172a 0%, #312e81 50%, #581c87 100%)',
  },
  rose: {
    label: 'Rose Gold',
    className: 'bg-gradient-to-r from-rose-200 via-pink-300 to-fuchsia-300',
    swatch: 'linear-gradient(90deg, #fecdd3 0%, #f9a8d4 50%, #f0abfc 100%)',
  },
  amber: {
    label: 'Amber Glow',
    className: 'bg-gradient-to-r from-amber-400 via-yellow-300 to-orange-300',
    swatch: 'linear-gradient(90deg, #fbbf24 0%, #fde047 50%, #fdba74 100%)',
  },
  monochrome: {
    label: 'Monochrome',
    className: 'bg-gradient-to-r from-slate-700 via-slate-500 to-slate-300',
    swatch: 'linear-gradient(90deg, #334155 0%, #64748b 50%, #cbd5e1 100%)',
  },
};

const DEFAULT_GRADIENT = 'cyan-violet';

/**
 * Resolve a banner image src against the current backend.
 * - "https://..." → pass through
 * - "/api/talent/banner/file/..." → prefix REACT_APP_BACKEND_URL so it works
 *   in shared/iframe contexts too.
 */
function resolveBannerImageSrc(value) {
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/api/')) {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}${value}`;
  }
  return value;
}

/**
 * Props:
 *   kind         : 'gradient' | 'color' | 'image' | null/undefined
 *   value        : preset key | hex | path/url | null
 *   offsetY      : 0-100 (vertical crop hint); only relevant for images
 *   className    : extra wrapper classes (e.g. 'h-20' for height — REQUIRED)
 *   children     : overlay content rendered above the banner
 *   testId       : optional data-testid
 */
export default function TalentBanner({
  kind,
  value,
  offsetY = 50,
  className = 'h-20',
  children,
  testId = 'talent-banner',
}) {
  const effectiveKind = kind || 'gradient';
  const effectiveValue = value || (effectiveKind === 'gradient' ? DEFAULT_GRADIENT : '');

  if (effectiveKind === 'image' && effectiveValue) {
    const src = resolveBannerImageSrc(effectiveValue);
    return (
      <div
        className={`relative bg-slate-200 ${className}`}
        style={{
          backgroundImage: `url("${src}")`,
          backgroundSize: 'cover',
          backgroundPosition: `center ${Math.max(0, Math.min(100, offsetY))}%`,
          backgroundRepeat: 'no-repeat',
        }}
        data-testid={testId}
        data-banner-kind="image"
      >
        {children}
      </div>
    );
  }

  if (effectiveKind === 'color' && effectiveValue) {
    return (
      <div
        className={`relative ${className}`}
        style={{ backgroundColor: effectiveValue }}
        data-testid={testId}
        data-banner-kind="color"
      >
        {children}
      </div>
    );
  }

  // gradient fallback
  const preset = GRADIENT_PRESETS[effectiveValue] || GRADIENT_PRESETS[DEFAULT_GRADIENT];
  return (
    <div
      className={`relative ${preset.className} ${className}`}
      data-testid={testId}
      data-banner-kind="gradient"
      data-banner-preset={effectiveValue}
    >
      {children}
    </div>
  );
}
